import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const TicketTrackingPanel = ({ tickets, onRefresh }) => {
  const [selectedStatus, setSelectedStatus] = useState('all');

  const filteredTickets = selectedStatus === 'all'
    ? tickets
    : tickets?.filter(t => t?.ticketStatus === selectedStatus);

  const getStatusBadge = (status) => {
    const statusStyles = {
      open: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'Open', icon: 'Inbox' },
      in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'In Progress', icon: 'Clock' },
      pending_client: { bg: 'bg-purple-500/10', text: 'text-purple-600', label: 'Pending Your Response', icon: 'MessageSquare' },
      resolved: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Resolved', icon: 'CheckCircle' },
      closed: { bg: 'bg-gray-500/10', text: 'text-gray-600', label: 'Closed', icon: 'XCircle' }
    };
    return statusStyles?.[status] || statusStyles?.open;
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      critical: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Critical' },
      high: { bg: 'bg-orange-500/10', text: 'text-orange-600', label: 'High' },
      medium: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', label: 'Medium' },
      low: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Low' }
    };
    return priorityStyles?.[priority] || priorityStyles?.medium;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTimeRemaining = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diff < 0) return 'Overdue';
    if (hours < 24) return `${hours}h ${minutes}m remaining`;
    const days = Math.floor(hours / 24);
    return `${days} days remaining`;
  };

  return (
    <div className="p-6">
      {/* Status Filter */}
      <div className="mb-6 flex items-center gap-2 flex-wrap">
        {['all', 'open', 'in_progress', 'pending_client', 'resolved', 'closed']?.map(status => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedStatus === status
                ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {status === 'all' ? 'All Tickets' : status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Tickets List */}
      <div className="space-y-4">
        {filteredTickets?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-foreground font-medium">No tickets found</p>
            <p className="text-sm text-muted-foreground mt-1">Submit a new ticket to get started</p>
          </div>
        ) : (
          filteredTickets?.map(ticket => {
            const statusBadge = getStatusBadge(ticket?.ticketStatus);
            const priorityBadge = getPriorityBadge(ticket?.priority);
            const timeRemaining = calculateTimeRemaining(ticket?.resolutionDueAt);

            return (
              <div key={ticket?.id} className="bg-muted/50 border border-border rounded-lg p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-foreground">{ticket?.ticketNumber}</h4>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge?.bg} ${statusBadge?.text}`}>
                        <Icon name={statusBadge?.icon} size={12} />
                        {statusBadge?.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityBadge?.bg} ${priorityBadge?.text}`}>
                        {priorityBadge?.label}
                      </span>
                    </div>
                    <p className="text-foreground font-medium mb-1">{ticket?.subject}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{ticket?.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {ticket?.category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {formatDate(ticket?.createdAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Time Remaining</p>
                    <p className={`text-sm font-medium mt-1 ${
                      timeRemaining === 'Overdue' ? 'text-red-600' : 'text-foreground'
                    }`}>
                      {timeRemaining || 'N/A'}
                    </p>
                  </div>
                </div>

                {ticket?.assignedAgent && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="User" size={14} />
                    <span>Assigned to: {ticket?.assignedAgent}</span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TicketTrackingPanel;